/* eslint-disable better-mutation/no-mutation */

import $ from 'jquery';
import { component } from 'flightjs';
import BubbledFocus from 'tradera/legacy/static/utils/bubbled-focus';
import { isServer } from 'tradera/utils/nextjs';

if (isServer) {
    // Do not remove this without also removing the disable of eslint better-mutation/no-mutation at top of file.
    throw new Error(
        'This implementation may not compatible with SSR where global properties are shared between server instances'
    );
}

function AbstractModal() {
    this.modalId = undefined;

    this.opts = {};

    this.defaultAttrs({
        closeModal: '[data-modal-close]',
        modalBackground: '[data-modal-background]',
        modalTemplate: '[data-modal-template]'
    });

    this._isActive = function () {
        if (
            this.$modal &&
            this.$modal.data('modalId') === this.modalId &&
            $('body').find('> ' + this.attr.modalBackground).length > 0 &&
            this.$modalHtml
        ) {
            return true;
        }
        return false;
    };

    this.triggerOpen = function (evt, evtData) {
        /* Triggered by event */
        this._open(evtData);
    };

    this.triggerClose = function (evt, evtData) {
        /* Triggered by event */
        this._close(evtData);
    };

    this.triggerSetNonDismissible = function (evt, evtData) {
        /* Triggered by event */
        this._setNonDismissible(evtData);
    };

    this.openModal = function (data) {
        /* Triggered from components extending this */
        this._open(data);
    };

    this._open = function (evtData) {
        this._cancelSpinner();
        if (this._isActive()) {
            this._updateModal(evtData);
        } else {
            this._createModal(evtData);
        }
    };

    this._cancelSpinner = function () {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    };

    this._updateModal = function (evtData) {
        evtData.cssClass = evtData.cssClass
            ? evtData.cssClass
            : 'modal-default';

        this.$modalContent.html(evtData.content);
        this.$modalHtml
            .attr('class', 'modal-background')
            .addClass(evtData.cssClass);
        if (evtData.adjustModalPosition) {
            evtData.adjustModalPosition(this);
        } else {
            this._adjustModalPosition();
        }

        if (evtData.nonDismissible) {
            this._setNonDismissible(true);
        }

        if (evtData.beforeShow) {
            evtData.beforeShow();
        }
    };

    this._createModal = function (evtData) {
        evtData.cssClass = evtData.cssClass
            ? evtData.cssClass
            : 'modal-default';

        this.$modalHtml = $(
            this.$node.find(this.attr.modalTemplate).clone().html()
        );
        this.$modal = this.$modalHtml.find('[data-modal]');

        this.modalId = 'id' + new Date().getTime();

        this.$modal.data('modalId', this.modalId);

        if (evtData.chromeless) {
            this.$modalContent = this.$modal;
            this.$modalContent.html(evtData.content);
        } else {
            this.$modalContent = this.$modalHtml.find('[data-modal-content]');
            this.$modalContent.html(evtData.content);
        }

        this.$modalHtml.addClass(evtData.cssClass);
        this.$node.append(this.$modalHtml);

        this._resizeModalHeight();
        if (evtData.adjustModalPosition) {
            evtData.adjustModalPosition(this);
        } else {
            this._adjustModalPosition();
        }

        if (evtData.nonDismissible) {
            this._setNonDismissible(true);
        }

        if (evtData.beforeShow) {
            evtData.beforeShow();
        }

        this._fadeIn();

        this.on(document, 'keyup', this._escButtonClose);
    };

    this._resizeModalHeight = function () {
        if (this._isActive()) {
            this.$modalHtml.css('height', $(document).height());
        }
    };

    this._escButtonClose = function (evt) {
        if (evt.which === 27 && !this._nonDismissible) {
            this._close();
        }
    };

    this._closeClick = function (evt) {
        if (!this._isActive()) {
            return;
        }
        if (this._closePrevented) {
            return;
        }

        const target = $(evt.target);
        if (target.attr('data-modal-close') === undefined) {
            return;
        }
        if (
            this._nonDismissible &&
            target.attr('data-modal-background') != undefined
        ) {
            return;
        }

        evt.stopPropagation();
        this._close();
    };

    this._close = function () {
        if (!this._isActive()) {
            return;
        }

        this.off(document, 'keyup', this._escButtonClose);
        this.trigger(document, 'ui:body-container:on-modal-close');

        this.$modalHtml.css('opacity', 0);
        setTimeout(
            function () {
                this._removeModal();
            }.bind(this),
            250
        );
    };

    this._removeModal = function () {
        if (this.$modalHtml) {
            this.$modalHtml.remove();
        }
    };

    this._adjustModalPosition = function () {
        const popupHeight = this.$modal.height();
        const scroll = $(document).scrollTop();
        const windowHeight =
            window.innerHeight || document.documentElement.clientHeight;

        if (popupHeight > windowHeight) {
            this.$modal.css('margin-top', scroll);
        } else {
            const targetOffset = (windowHeight - popupHeight) * 0.25;
            this.$modal.css('margin-top', targetOffset + scroll);
        }
    };

    this._fadeIn = function () {
        this.$modalHtml.css('opacity', 1);
    };

    this._preventClose = function () {
        this._closePrevented = true;
    };

    this._enableClose = function () {
        setTimeout(
            function () {
                this._closePrevented = false;
            }.bind(this),
            100
        );
    };

    this._setNonDismissible = function (value) {
        this._nonDismissible = value;
        if (value) {
            this.$modalHtml.addClass('modal-close-disabled');
        } else {
            this.$modalHtml.removeClass('modal-close-disabled');
        }
    };

    this.after('initialize', function () {
        this.on(
            document,
            'ui:error:show ui:saved-search-modal:after-added',
            this._close
        );
        this.on('click', {
            closeModal: this._closeClick
        });

        this.on(document, 'ui:teardown', this._removeModal);
        this.on(
            document,
            'ui:recently-viewed-items:on-items-change',
            this._resizeModalHeight
        );

        this.focus('[data-modal] input', this._preventClose.bind(this));
        this.blur('[data-modal] input', this._enableClose.bind(this));
    });
}

export default component(AbstractModal, BubbledFocus);
